import { useCallback, useContext, useMemo, useEffect, useRef, useState } from "react";
import { ConfirmDialog } from "../../../../components/shared";
import { useFormikContext } from "formik";
import { AssignGroupContext } from "./context";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import { OwcButton, OwcEditableDropdown, OwcListItem } from "@one/react";
import { withApollo } from "react-apollo";
import {
  renderModelDropdownItems,
  createModelSelectHandler,
  createModelInputChangeHandler,
  createModelClearHandler,
  syncModelWithFormikValue,
  refreshModelPopover,
  getModelLabel,
  fetchInstrumentModels,
  toNullableValue,
  getModelFieldValue,
  loadModelsOnDialogOpen,
  syncModelOptionsState,
} from "./GroupDialog.model.utils";
import {
  loadEditModeLocationData,
  syncEditModeDropdownValues,
  trackInitialRoomValue,
  restoreInitialRoomIfAvailable,
  initializeDialogState,
  resetInteractionOnEditTargetSwitch,
  createSiteSelectChangeHandler,
  createBuildingSelectChangeHandler,
  createFloorSelectChangeHandler,
  createRoomSelectChangeHandler,
  getFloorDropdownKey,
  getRoomDropdownKey,
  isSiteDropdownDisabled,
  isBuildingDropdownDisabled,
  isFloorDropdownDisabled,
  isRoomDropdownDisabled,
  getNamedItemLabelWithId,
} from "./GroupDialog.location.utils";
import {
  isSubmitDisabled,
  sortByKeyAlpha,
  getApproveText,
  getDialogTitle,
  createGroupSelectChangeHandler,
  buildGroupOptionKey,
} from "./GroupDialog.form.utils";

export const CreateFieldStyled = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 8px;
  padding-bottom: 32px;
  margin-bottom: 20px;
`;

export const FieldRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  width: 100%;

  & > .selectBox,
  .TextFieldInput {
    margin: 0;
    margin-right: 20px;
    min-width: 48%;
    max-width: 100%;
  }

  & > .searchTextMultiSelect {
    width: 50%;
  }
`;

export { renderModelDropdownItems, loadEditModeLocationData } from "./GroupDialog.utils";

const GroupDialog = ({
  client,
  onCancel,
  submit,
  groupList,
  setSelectedEquipment,
  onSiteChange,
  onBuildingChange,
  onFloorChange,
}) => {
  const formik = useFormikContext();
  const { dialogOpen, isEditMode } = useContext(AssignGroupContext);

  const floorDropdownRef = useRef(null);
  const roomDropdownRef = useRef(null);
  const modelDropdownRef = useRef(null);

  const initialRoomValue = useRef(null);
  const isMountedRef = useRef(true);
  const isLoadingDataRef = useRef(false);

  // Use updated paths to access our Redux state
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const { sites, adminBuildingsList, adminFloorsList, adminRoomsList } = useSelector(
    (state) => state.bookingAdmin.locationData
  );
  const { instruments: filteredInstruments, loading: isLoadingModels } = useSelector(
    (state) => state.bookingAdmin.filteredInstruments
  );
  const [instrumentModels, setInstrumentModels] = useState([]);
  const [modelSearchTerm, setModelSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState("");
  const isUpdatingModelOptionsRef = useRef(false);
  const suppressSyntheticEmptyInputRef = useRef(false);

  const sortedGroupList = useMemo(() => sortByKeyAlpha(groupList, "groupName"), [groupList]);
  const sortedSites = useMemo(() => sortByKeyAlpha(sites, "siteName"), [sites]);
  const sortedBuildings = useMemo(() => sortByKeyAlpha(adminBuildingsList, "name"), [adminBuildingsList]);
  const sortedFloors = useMemo(() => sortByKeyAlpha(adminFloorsList, "name"), [adminFloorsList]);
  const sortedRooms = useMemo(() => {
    const sorted = sortByKeyAlpha(adminRoomsList, "name");
    return sorted;
  }, [adminRoomsList]);

  useEffect(
    () =>
      syncEditModeDropdownValues({
        isEditMode,
        dialogOpen,
        floorDropdownRef,
        roomDropdownRef,
        formik,
        sortedFloors,
        sortedRooms,
      }),
    [isEditMode, dialogOpen, formik, formik.values?.floor, formik.values?.room, sortedFloors, sortedRooms]
  );

  const previousRoomValue = useRef(null);
  useEffect(
    () => trackInitialRoomValue({ previousRoomValue, formik, initialRoomValue, isEditMode }),
    [formik, formik.values?.room, isEditMode]
  );

  const originalSetFieldValue = formik.setFieldValue;
  formik.setFieldValue = (field, value, shouldValidate) => {
    return originalSetFieldValue(field, value, shouldValidate);
  };

  useEffect(
    () => restoreInitialRoomIfAvailable({ isEditMode, sortedRooms, formik, initialRoomValue, isMountedRef }),
    [isEditMode, sortedRooms, formik.values?.room, formik]
  );

  const handleGroupSelectChange = useMemo(
    () => createGroupSelectChangeHandler({ sortedGroupList, formik }),
    [sortedGroupList, formik]
  );

  // Load instrument models based on search term
  const loadInstrumentModels = useCallback(
    async (searchTerm) => fetchInstrumentModels({ client, user, dispatch, searchTerm }),
    [client, user, dispatch]
  );

  useEffect(() => {
    loadModelsOnDialogOpen({ dialogOpen, isEditMode, loadInstrumentModels, modelSearchTerm });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, isEditMode, loadInstrumentModels, modelSearchTerm]);

  // Update local instrumentModels when Redux state changes
  useEffect(() => {
    syncModelOptionsState({
      filteredInstruments,
      isUpdatingModelOptionsRef,
      suppressSyntheticEmptyInputRef,
      setInstrumentModels,
    });
  }, [filteredInstruments, isEditMode]);

  const hasUserInteractedWithSite = useRef(false);
  const lastEditGroupId = useRef(null);
  const isInitialLoad = useRef(true);
  const initialValuesLoaded = useRef(false);

  useEffect(
    () =>
      initializeDialogState({
        isEditMode,
        dialogOpen,
        hasUserInteractedWithSite,
        initialRoomValue,
        initialValuesLoaded,
        loadedDataRef,
        formik,
        isInitialLoad,
        setInputValue,
        setModelSearchTerm,
      }),
    [
      isEditMode,
      dialogOpen,
      formik.values?.site,
      formik.values?.building,
      formik.values?.floor,
      formik.values?.room,
      formik.values?.model,
        formik,
    ]
  );

  const loadedDataRef = useRef({
    site: null,
    building: null,
    floor: null,
  });

  // Load location data in sequence when in edit mode
  useEffect(() => {
    loadEditModeLocationData({
      isEditMode,
      dialogOpen,
      isLoadingDataRef,
      formik,
      adminBuildingsList,
      adminFloorsList,
      adminRoomsList,
      loadedDataRef,
      sortedBuildings,
      sortedFloors,
      onSiteChange,
      onBuildingChange,
      onFloorChange,
      isMountedRef,
    });
  }, [
    isEditMode,
    dialogOpen,
    formik,
    formik.values?.site,
    formik.values?.building,
    formik.values?.floor,
    adminBuildingsList,
    adminFloorsList,
    adminRoomsList,
    adminBuildingsList.length,
    adminFloorsList.length,
    adminRoomsList.length,
    sortedBuildings,
    sortedFloors,
    onSiteChange,
    onBuildingChange,
    onFloorChange,
  ]);

  const handleModelSelectChange = useMemo(
    () => createModelSelectHandler({ instrumentModels, setSelectedEquipment, formik, setInputValue, setModelSearchTerm }),
    [instrumentModels, setSelectedEquipment, formik]
  );

  const handleModelInputChange = useMemo(
    () => createModelInputChangeHandler({
      isEditMode,
      isUpdatingModelOptionsRef,
      suppressSyntheticEmptyInputRef,
      setInputValue,
      setModelSearchTerm,
      formik,
      setSelectedEquipment,
    }),
    [isEditMode, formik, setSelectedEquipment]
  );

  const handleModelClear = useMemo(
    () => createModelClearHandler({ isEditMode, formik, setInputValue, setModelSearchTerm, setSelectedEquipment }),
    [isEditMode, formik, setSelectedEquipment]
  );

  const handleSiteSelectChange = useMemo(
    () => createSiteSelectChangeHandler({
      sortedSites,
      formik,
      isInitialLoad,
      initialValuesLoaded,
      isEditMode,
      hasUserInteractedWithSite,
      isMountedRef,
      isLoadingDataRef,
      onSiteChange,
    }),
    [sortedSites, formik, isEditMode, onSiteChange]
  );

  const handleBuildingSelectChange = useMemo(
    () => createBuildingSelectChangeHandler({
      sortedBuildings,
      formik,
      isInitialLoad,
      initialValuesLoaded,
      isEditMode,
      isMountedRef,
      isLoadingDataRef,
      onBuildingChange,
    }),
    [sortedBuildings, formik, isEditMode, onBuildingChange]
  );

  const handleFloorSelectChange = useMemo(
    () => createFloorSelectChangeHandler({
      sortedFloors,
      formik,
      isInitialLoad,
      initialValuesLoaded,
      isEditMode,
      isMountedRef,
      isLoadingDataRef,
      onFloorChange,
    }),
    [sortedFloors, formik, isEditMode, onFloorChange]
  );

  const handleRoomSelectChange = useMemo(
    () => createRoomSelectChangeHandler({ sortedRooms, formik }),
    [sortedRooms, formik]
  );

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    syncModelWithFormikValue({ formik, instrumentModels, setInstrumentModels });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrumentModels, formik.values.model]);

  useEffect(
    () =>
      refreshModelPopover({
        isEditMode,
        dialogOpen,
        isLoadingModels,
        modelDropdownRef,
        instrumentModels,
      }),
    [isEditMode, dialogOpen, isLoadingModels, instrumentModels, modelSearchTerm, inputValue]
  );

  useEffect(
    () =>
      resetInteractionOnEditTargetSwitch({
        isEditMode,
        dialogOpen,
        formik,
        lastEditGroupId,
        hasUserInteractedWithSite,
        isInitialLoad,
      }),
    [isEditMode, dialogOpen, formik.values.model, formik]
  );

  const approveText = getApproveText(isEditMode);
  const dialogTitle = getDialogTitle(isEditMode);
  const modelLabel = getModelLabel(isLoadingModels);
  const isApproveDisabled = isSubmitDisabled(formik.values);
  const groupValue = toNullableValue(formik.values?.group);
  const modelValue = getModelFieldValue({
    isEditMode,
    formModelValue: formik.values?.model,
    inputValue,
  });
  const siteValue = toNullableValue(formik.values?.site);
  const buildingValue = toNullableValue(formik.values?.building);
  const floorValue = toNullableValue(formik.values?.floor);
  const roomValue = toNullableValue(formik.values?.room);
  const siteDisabled = isSiteDropdownDisabled({ isEditMode, groupValue: formik.values?.group });
  const buildingDisabled = isBuildingDropdownDisabled({
    isEditMode,
    buildingsLength: adminBuildingsList.length,
    siteValue: formik.values?.site,
  });
  const floorDisabled = isFloorDropdownDisabled({
    isEditMode,
    floorsLength: adminFloorsList.length,
    buildingValue: formik.values?.building,
  });
  const roomDisabled = isRoomDropdownDisabled({
    isEditMode,
    roomsLength: adminRoomsList.length,
    floorValue: formik.values?.floor,
  });
  const floorDropdownKey = getFloorDropdownKey(sortedFloors.length, formik.values?.floor);
  const roomDropdownKey = getRoomDropdownKey(sortedRooms.length, formik.values?.room);

  return (
    <ConfirmDialog
      open={dialogOpen}
      close={onCancel}
      approveText={approveText}
      approveColor="primary"
      approveVariant="contained"
      cancelText="Cancel"
      cancelVariant="outlined"
      cancelColor="primary"
      onCancel={() => {
        formik.resetForm();
        onCancel();
      }}
      isDivider
      maxWidth="sm"
      fullWidth
      testid={"import-file-dialog"}
      title={dialogTitle}
      renderActions={({ cancelColor, cancelVariant, approveColor, approveVariant, cancelText, approveText }) => (
        <div style={{ display: "flex", marginLeft: "auto" }}>
          <OwcButton
            data-testid="confirm-dialog-actions-button-cancel"
            onClick={() => {
              formik.resetForm();
              onCancel();
            }}
            variant="secondary"
            style={{ marginRight: 32 }}
          >
            {cancelText}
          </OwcButton>
          <OwcButton
            data-testid="confirm-dialog-actions-button-approve"
            onClick={() => {
              submit(formik.values);
            }}
            variant="primary"
            disabled={isApproveDisabled}
          >
            {approveText}
          </OwcButton>
        </div>
      )}
    >
      <CreateFieldStyled>
        {/* Row 1 */}
        <FieldRow>
          <OwcEditableDropdown
            required={true}
            key="group-dropdown"
            className="selectBox"
            name="group"
            label="Select booking group name *"
            value={groupValue}
            data-testid="booking-filter-admin-reserved-for-email"
            onSelectChange={handleGroupSelectChange}
          >
            {sortedGroupList?.map((item, index) => {
              const option = item.groupName;
              return (
                <OwcListItem value={option} key={buildGroupOptionKey(item, index)}>
                  {option}
                </OwcListItem>
              );
            })}
          </OwcEditableDropdown>
          <OwcEditableDropdown
            ref={modelDropdownRef}
            required={true}
            key={`model-dropdown-${isEditMode}`}
            className="selectBox"
            name="model"
            label={modelLabel}
            value={modelValue}
            disabled={isEditMode}
            clearable={!isEditMode}
            onSelectChange={handleModelSelectChange}
            onInputChange={handleModelInputChange}
            onClear={handleModelClear}
          >
            {renderModelDropdownItems({
              isLoadingModels,
              instrumentModels,
              modelSearchTerm,
            })}
          </OwcEditableDropdown>
        </FieldRow>

        {/* Row 2 */}
        <FieldRow>
          <OwcEditableDropdown
            required={true}
            key="site-dropdown"
            className="selectBox"
            name="site"
            label="Select Site *"
            value={siteValue}
            disabled={siteDisabled}
            onSelectChange={handleSiteSelectChange}
          >
            {sortedSites?.map((site) => (
              <OwcListItem key={site.siteId} value={site.siteName}>
                {site.siteName}
              </OwcListItem>
            ))}
          </OwcEditableDropdown>

          <OwcEditableDropdown
            required={true}
            key="building-dropdown"
            className="selectBox"
            name="building"
            label="Select Building *"
            value={buildingValue}
            disabled={buildingDisabled}
            onSelectChange={handleBuildingSelectChange}
          >
            {sortedBuildings.map((item) => (
              <OwcListItem key={item.id} value={item.name}>
                {getNamedItemLabelWithId(item, "buildingId")}
              </OwcListItem>
            ))}
          </OwcEditableDropdown>
        </FieldRow>

        {/* Row 3 */}
        <FieldRow>
          <OwcEditableDropdown
            ref={floorDropdownRef}
            key={floorDropdownKey}
            required={true}
            className="selectBox"
            name="floor"
            label="Select Floor *"
            value={floorValue}
            disabled={floorDisabled}
            onSelectChange={handleFloorSelectChange}
          >
            {sortedFloors.map((item) => (
              <OwcListItem key={item.id} value={item.name}>
                {getNamedItemLabelWithId(item, "id")}
              </OwcListItem>
            ))}
          </OwcEditableDropdown>

          <OwcEditableDropdown
            ref={roomDropdownRef}
            key={roomDropdownKey}
            required={true}
            className="selectBox"
            name="room"
            label="Select Room *"
            value={roomValue}
            disabled={roomDisabled}
            onSelectChange={handleRoomSelectChange}
          >
            {sortedRooms.map((item) => (
              <OwcListItem key={item.id} value={item.name}>
                {getNamedItemLabelWithId(item, "id")}
              </OwcListItem>
            ))}
          </OwcEditableDropdown>
        </FieldRow>
      </CreateFieldStyled>
    </ConfirmDialog>
  );
};

export default withApollo(GroupDialog);
